import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, MonitorSmartphone, Wifi, WifiOff, BellRing, Building2, MapPin } from 'lucide-react';
import { SEVERITY_STYLES, ALERT_TYPE_LABELS, fmtWhen } from './rmmUtils';

const Stat = ({ label, value, icon: Icon, color, testId }) => (
  <Card className="bg-white border-slate-200">
    <CardContent className="p-4 flex items-center justify-between">
      <div>
        <p className="text-xs text-slate-500">{label}</p>
        <p className="text-2xl font-bold text-slate-800" data-testid={testId}>{value ?? '—'}</p>
      </div>
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${color}`}>
        <Icon className="w-5 h-5" />
      </div>
    </CardContent>
  </Card>
);

export const OverviewTab = ({ summary }) => {
  if (!summary) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-cyan-500" /></div>;

  return (
    <div className="space-y-6" data-testid="rmm-overview-tab">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <Stat label="Customers" value={summary.customers} icon={Building2} color="bg-cyan-100 text-cyan-600" testId="rmm-stat-customers" />
        <Stat label="Sites" value={summary.sites} icon={MapPin} color="bg-teal-100 text-teal-600" testId="rmm-stat-sites" />
        <Stat label="Devices" value={summary.devices_total} icon={MonitorSmartphone} color="bg-slate-100 text-slate-600" testId="rmm-stat-devices" />
        <Stat label="Online" value={summary.devices_online} icon={Wifi} color="bg-emerald-100 text-emerald-600" testId="rmm-stat-online" />
        <Stat label="Offline" value={summary.devices_offline} icon={WifiOff} color="bg-red-100 text-red-600" testId="rmm-stat-offline" />
        <Stat label="Open Alerts" value={summary.open_alerts} icon={BellRing} color="bg-amber-100 text-amber-600" testId="rmm-stat-alerts" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-white border-slate-200">
          <CardHeader><CardTitle className="text-slate-800 text-lg">Recent Alerts</CardTitle></CardHeader>
          <CardContent className="space-y-2" data-testid="rmm-recent-alerts">
            {(summary.recent_alerts || []).length === 0 && <p className="text-sm text-slate-400 py-2">No alerts yet — enroll a device to start monitoring.</p>}
            {(summary.recent_alerts || []).map((a) => (
              <div key={a.id} className="flex items-center justify-between p-2.5 rounded-lg border border-slate-200 gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <Badge className={SEVERITY_STYLES[a.severity] || ''}>{a.severity}</Badge>
                    <span className="text-sm font-medium text-slate-800 truncate">{ALERT_TYPE_LABELS[a.type] || a.type}</span>
                  </div>
                  <p className="text-xs text-slate-500 truncate">{a.device_hostname} · {a.customer_name || '—'} · {fmtWhen(a.created_at)}</p>
                </div>
                <Badge variant="secondary" className="capitalize shrink-0">{a.status}</Badge>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card className="bg-white border-slate-200">
          <CardHeader><CardTitle className="text-slate-800 text-lg">Recent Activity</CardTitle></CardHeader>
          <CardContent className="space-y-2" data-testid="rmm-recent-audit">
            {(summary.recent_audit || []).length === 0 && <p className="text-sm text-slate-400 py-2">No RMM activity yet.</p>}
            {(summary.recent_audit || []).map((l) => (
              <div key={l.id} className="p-2.5 rounded-lg border border-slate-200">
                <p className="text-sm text-slate-800">
                  <span className="font-medium">{l.actor_name || 'System / Agent'}</span>
                  <span className="text-slate-500"> — {(l.action || '').replaceAll('_', ' ')}</span>
                </p>
                <p className="text-xs text-slate-400 truncate">{l.details} · {fmtWhen(l.created_at)}{l.ip ? ` · ${l.ip}` : ''}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
